import React, { useState } from "react";
import DefaultButton from "../../buttons/defaultButton";
import DefaultBadge from "../../badge/defaultBadge";
import InlineCellEditor from "./InlineCellEditor";
import { HiCheckCircle, HiExclamationCircle, HiXCircle } from "react-icons/hi";

const ImportPreview = ({
  previewData,
  validationResults,
  columnMapping,
  onStartImport,
  onBack,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editedData, setEditedData] = useState({});

  const handleCellEdit = (rowNumber, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [`${rowNumber}-${field}`]: value,
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ok":
        return <HiCheckCircle className="text-success-600" />;
      case "warning":
        return <HiExclamationCircle className="text-warning-600" />;
      case "error":
        return <HiXCircle className="text-error-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ok":
        return <DefaultBadge status={1} custom="Valid" />;
      case "warning":
        return <DefaultBadge status={0} custom="Warning" />;
      case "error":
        return <DefaultBadge status={2} custom="Error" />;
      default:
        return null;
    }
  };

  const getRowData = (row) => {
    const rowKey = `${row.row_number}`;
    const edited = editedData[`${row.row_number}-${Object.keys(row.data)[0]}`];
    return edited ? { ...row.data, ...edited } : row.data;
  };

  const validationRow = (rowNumber) => {
    return validationResults?.rows?.find((r) => r.row_number === rowNumber);
  };

  const displayedColumns = Object.keys(columnMapping).filter(
    (col) => columnMapping[col]
  );

  return (
    <div className="flex flex-col gap-6" role="region" aria-label="Import preview">
      <div>
        <h3 className="text-lg font-semibold text-theme mb-2">Preview & Validation</h3>
        <p className="text-sm text-textLight">
          Review the first 5 rows and validation results. You can edit cells before importing.
        </p>
      </div>

      {/* Validation Summary */}
      {validationResults && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white border border-borderColor rounded-lg p-4">
            <p className="text-xs text-textLight mb-1">Total Rows</p>
            <p className="text-2xl font-semibold text-theme">{validationResults.total}</p>
          </div>
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-xs text-success-700 mb-1">Valid</p>
            <p className="text-2xl font-semibold text-success-900">
              {validationResults.ok}
            </p>
          </div>
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <p className="text-xs text-warning-700 mb-1">Warnings</p>
            <p className="text-2xl font-semibold text-warning-900">
              {validationResults.warning}
            </p>
          </div>
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-xs text-error-700 mb-1">Errors</p>
            <p className="text-2xl font-semibold text-error-900">
              {validationResults.error}
            </p>
          </div>
        </div>
      )}

      {/* Preview Table */}
      <div className="bg-white border border-borderColor rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" role="table" aria-label="Preview data table">
            <thead className="bg-primary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Row
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Status
                </th>
                {displayedColumns.map((col) => (
                  <th
                    key={col}
                    className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase"
                  >
                    {columnMapping[col]?.replace(/_/g, " ") || col}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                  Issues
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderColor">
              {previewData.map((row) => {
                const validation = validationRow(row.row_number);
                const rowData = getRowData(row);
                const isEditing = editingCell?.row === row.row_number;

                return (
                  <tr
                    key={row.row_number}
                    className={`
                      ${validation?.status === "error" ? "bg-error-50" : ""}
                      ${validation?.status === "warning" ? "bg-warning-50" : ""}
                    `}
                  >
                    <td className="px-4 py-3 text-sm text-theme font-medium">
                      {row.row_number}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(validation?.status)}
                        {getStatusBadge(validation?.status)}
                      </div>
                    </td>
                    {displayedColumns.map((col) => {
                      const fieldKey = columnMapping[col];
                      const value = rowData[fieldKey] || "";
                      const cellKey = `${row.row_number}-${fieldKey}`;
                      const isEditingThisCell =
                        editingCell?.row === row.row_number &&
                        editingCell?.field === fieldKey;

                      return (
                        <td
                          key={col}
                          className="px-4 py-3 text-sm text-theme"
                          onDoubleClick={() =>
                            setEditingCell({ row: row.row_number, field: fieldKey })
                          }
                        >
                          {isEditingThisCell ? (
                            <InlineCellEditor
                              value={value}
                              field={fieldKey}
                              onSave={(newValue) => {
                                handleCellEdit(row.row_number, fieldKey, newValue);
                                setEditingCell(null);
                              }}
                              onCancel={() => setEditingCell(null)}
                            />
                          ) : (
                            <span className="cursor-pointer hover:bg-primary-100 px-2 py-1 rounded">
                              {value || "-"}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-xs">
                      {validation?.errors?.length > 0 && (
                        <div className="text-error-700">
                          {validation.errors.join(", ")}
                        </div>
                      )}
                      {validation?.warnings?.length > 0 && (
                        <div className="text-warning-700">
                          {validation.warnings.join(", ")}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Instructions */}
      <div className="bg-information-50 border border-information-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HiExclamationCircle className="text-information-600 text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-information-900 mb-1">
              Edit Cells
            </p>
            <p className="text-xs text-information-700">
              Double-click any cell to edit its value. Changes will be applied during import.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <DefaultButton variant="secondary" name="Back to Mapping" onClick={onBack} />
        <div className="flex gap-3">
          <DefaultButton
            variant="default"
            name="Start Import"
            onClick={onStartImport}
            disabled={validationResults?.error >= validationResults?.total}
          />
        </div>
      </div>
    </div>
  );
};

export default ImportPreview;

